import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { GameGameService } from '../../../services/games/game-game/game-game.service';
import { LangSrcService } from '../../../services/lang-src/lang-src.service';
import { AudioService } from '../../../services/audio/audio.service';
import { FullscreenService } from '../../../services/fullscreen/fullscreen.service';
import { PageService } from '../../../services/page/page.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { MetaService } from '../../../services/meta/meta.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { GAME_TEMPLATE_REGISTRY } from './game-template-registry';
import { GAME_ANSWER_REGISTRY, DEFAULT_ANSWER_LOADER } from './game-answer-registry';
import {ContentContextService} from '../../../services/content-context/content-context.service';

@Component({
  selector: 'app-games-game',
  standalone: true,
  imports: [CommonModule, SpinnerComponent],
  templateUrl: './games-game.component.html',
  styleUrls: ['./games-game.component.scss'],
})
export class GamesGameComponent implements OnInit, OnDestroy {

  loading: boolean;
  answersVerify: boolean;
  endOfGame: boolean;
  game: any;
  gameResult: any;

  @ViewChild('dynamicHost', { read: ViewContainerRef }) dynamicHost: ViewContainerRef;

  private subscriptions: Subscription[] = [];

  constructor(
    public deviceSrv: DeviceDetectorService,
    public route: ActivatedRoute,
    private modal: NgbModal,
    private gameSrv: GameGameService,
    public langSrcSrv: LangSrcService,
    public audioSrv: AudioService,
    private router: Router,
    private fullscreenSrv: FullscreenService,
    private pageSrv: PageService,
    private metaSrv: MetaService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private contentContextSrv: ContentContextService,
  ) {}

  async ngOnInit() {
    this.translate.get(`Chargement...`).subscribe((value: string) => {
      this.metaSrv.setMetas({ title: value });
    });
    await this.startGame();
  }

  async ngOnDestroy() {
    this.fullscreenSrv.close();
    this.clearDynamicHost();
    this.contentContextSrv.clear();
  }

  async startGame() {
    this.loading = true;
    this.endOfGame = false;
    this.clearDynamicHost();

    this.game = await this.gameSrv.findById(
      this.route.snapshot.params.idLevel,
      this.route.snapshot.params.idGame,
    );

    if (!this.game) {
      await this.router.navigate([
        '/',
        this.langSrcSrv.current.shortCode,
        'games',
        this.route.snapshot.params.idSection,
        this.route.snapshot.params.idLevel,
      ]);
      return;
    }

    this.metaSrv.setMetas({ title: this.game.game.name });
    this.contentContextSrv.set({
      type: 'game',
      items: [{id: this.route.snapshot.params.idGame, name: this.game.game.name}],
    });
    this.loading = false;
    this.cdr.detectChanges(); // ensure *ngIf="!loading" and #dynamicHost are ready
    await this.loadGameTemplate();
  }

  async answersSubmit(event: { answers: any[] }) {
    this.clearDynamicHost();
    this.answersVerify = true;
    try {
      this.gameResult = await this.gameSrv.submitAnswers(event.answers).toPromise();
      this.endOfGame = true;
      this.answersVerify = false;
      await this.loadAnswerTemplate();
    } catch (error) {
      alert(error.toString());
      this.answersVerify = false;
      await this.loadGameTemplate(); // restore game on error
    }
  }

  async answersDifficultySubmit(event: { answers: any[]; difficulty: string }) {
    this.clearDynamicHost();
    this.answersVerify = true;
    try {
      this.gameResult = await this.gameSrv.submitAnswersAndDifficulty(event.answers, event.difficulty).toPromise();
      this.endOfGame = true;
      this.answersVerify = false;
      await this.loadAnswerTemplate();
    } catch (error) {
      alert(error.toString());
      this.answersVerify = false;
      await this.loadGameTemplate(); // restore game on error
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private clearDynamicHost(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    if (this.dynamicHost) {
      this.dynamicHost.clear();
    }
  }

  private safeSetInput(ref: ComponentRef<any>, name: string, value: any): void {
    try {
      ref.setInput(name, value);
    } catch {
      // Input not declared on this component — skip silently
    }
  }

  private async loadGameTemplate(): Promise<void> {
    const entry = GAME_TEMPLATE_REGISTRY[this.game.template];
    if (!entry) {
      // No registered template — render a fallback message via the host
      console.warn(`[GamesGame] No template registered for: ${this.game.template}`);
      return;
    }

    const ComponentClass = await entry.loader();
    this.dynamicHost.clear();
    const ref = this.dynamicHost.createComponent(ComponentClass);
    const instance = ref.instance as any;

    // Common inputs
    const omit = new Set(entry.omitInputs ?? []);
    const params = this.route.snapshot.params;
    this.safeSetInput(ref, 'idSection', params.idSection);
    this.safeSetInput(ref, 'idTheme', params.idTheme);
    this.safeSetInput(ref, 'idLevel', params.idLevel);
    this.safeSetInput(ref, 'gameName', this.game.game.name);
    if (!omit.has('gameDesc'))      this.safeSetInput(ref, 'gameDesc', this.game.game.description);
    if (!omit.has('gameDescAudio')) this.safeSetInput(ref, 'gameDescAudio', this.game.game.audioDesktop);
    this.safeSetInput(ref, 'questions', this.game.questions);

    // Static extra inputs
    if (entry.inputs) {
      for (const [k, v] of Object.entries(entry.inputs)) {
        this.safeSetInput(ref, k, v);
      }
    }

    // Dynamic extra inputs
    if (entry.inputsFn) {
      for (const [k, v] of Object.entries(entry.inputsFn(this.game))) {
        this.safeSetInput(ref, k, v);
      }
    }

    // Output subscription
    const submit = entry.submit;
    if (submit !== null) {
      if (submit === 'play') {
        const sub = instance.playSubmit?.subscribe(() => this.startGame());
        if (sub) this.subscriptions.push(sub);
      } else if (submit === 'difficulty') {
        const sub = instance.answersSubmit?.subscribe((e: any) => this.answersDifficultySubmit(e));
        if (sub) this.subscriptions.push(sub);
      } else {
        const sub = instance.answersSubmit?.subscribe((e: any) => this.answersSubmit(e));
        if (sub) this.subscriptions.push(sub);
      }
    }

    ref.changeDetectorRef.detectChanges();

    // Prefetch the answer component in the background so it's already cached
    // when the user submits — avoids a blank screen between submit and result.
    this.prefetchAnswerComponent();
  }

  private prefetchAnswerComponent(): void {
    const entry = GAME_ANSWER_REGISTRY[this.game.template];
    const loader = entry?.loader ?? DEFAULT_ANSWER_LOADER;
    loader().catch(() => {});
  }

  private async loadAnswerTemplate(): Promise<void> {
    const entry = GAME_ANSWER_REGISTRY[this.game.template];
    const loader = entry?.loader ?? DEFAULT_ANSWER_LOADER;

    const ComponentClass = await loader();
    this.dynamicHost.clear();
    const ref = this.dynamicHost.createComponent(ComponentClass);
    const instance = ref.instance as any;

    // Common inputs
    const params = this.route.snapshot.params;
    this.safeSetInput(ref, 'idSection', params.idSection);
    this.safeSetInput(ref, 'idTheme', params.idTheme);
    this.safeSetInput(ref, 'idLevel', params.idLevel);
    this.safeSetInput(ref, 'gameName', this.game.game.name);
    this.safeSetInput(ref, 'gameResult', this.gameResult);

    // Static extra inputs
    if (entry?.inputs) {
      for (const [k, v] of Object.entries(entry.inputs)) {
        this.safeSetInput(ref, k, v);
      }
    }

    // restartGame output
    const sub = instance.restartGame?.subscribe(() => this.startGame());
    if (sub) this.subscriptions.push(sub);

    ref.changeDetectorRef.detectChanges();
  }
}
